import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Search,
  X,
  Plus,
  Check,
  Folder,
  Trash2,
  Edit2,
  Briefcase,
  Filter,
} from "lucide-react";
import { fetchWrapper } from "src/helpers/fetch-wrapper";
import {
  getLocalStorageUser,
  toTitleCase,
} from "src/helpers/api/sharedFunction";

import { Universe, Stock } from "src/hooks/useCustomUniverses";
import { alertService } from "src/services";
import Pagination from "src/common/pagination";

export interface CustomUniverseManagerProps {
  universes: Universe[];
  setUniverses: React.Dispatch<React.SetStateAction<Universe[]>>;
  universeStocks: Record<string, Stock[]>;
  setUniverseStocks: React.Dispatch<
    React.SetStateAction<Record<string, Stock[]>>
  >;
  fetchUniverseStocks: (universeId: string, force?: boolean) => Promise<void>;
  isFetchingStocks: boolean;
  onMutate?: () => void;
}

const MAX_UNIVERSES = Number(process.env.NEXT_PUBLIC_MAX_UNIVERSES || 5);
const MAX_STOCKS_PER_UNIVERSE = Number(
  process.env.NEXT_PUBLIC_MAX_STOCKS_PER_UNIVERSE || 50,
);

const CustomUniverseManager = ({
  universes,
  setUniverses,
  universeStocks,
  setUniverseStocks,
  fetchUniverseStocks,
  isFetchingStocks,
  onMutate,
}: CustomUniverseManagerProps) => {
  const [selectedUniverse, setSelectedUniverse] = useState<Universe | null>(
    null,
  );

  const indices: string[] = [
    "Derivatives",
    "Nifty 500",
    "Nifty Micro 250",
    "Others",
    "Penny Stocks",
  ];

  // Universe form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUniverseName, setNewUniverseName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [universeToDelete, setUniverseToDelete] = useState<Universe | null>(
    null,
  );

  // Stock search & selection state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Stock[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hoveredStockId, setHoveredStockId] = useState<string | null>(null);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  // Draft states
  const [draftStocks, setDraftStocks] = useState<Stock[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const originalStocks = useMemo(() => {
    if (!selectedUniverse) return [];
    return universeStocks[selectedUniverse.id] || [];
  }, [selectedUniverse, universeStocks]);

  const hasUnsavedChanges = useMemo(() => {
    if (!selectedUniverse || isFetchingStocks || !isDirty) return false;
    if (originalStocks.length !== draftStocks.length) return true;
    const originalSet = new Set(originalStocks.map((s) => s.symbol));
    return draftStocks.some((s) => !originalSet.has(s.symbol));
  }, [
    draftStocks,
    originalStocks,
    selectedUniverse,
    isFetchingStocks,
    isDirty,
  ]);

  const pendingActionRef = useRef<(() => void) | null>(null);
  const [pendingActionMessage, setPendingActionMessage] =
    useState<React.ReactNode>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const handleActionWithUnsavedCheck = (
    action: () => void,
    message: React.ReactNode,
  ) => {
    if (hasUnsavedChanges) {
      pendingActionRef.current = action;
      setPendingActionMessage(message);
      setShowUnsavedModal(true);
    } else {
      action();
    }
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const stockListRef = useRef<HTMLDivElement>(null);

  // Sector filtering state
  const [sectors, setSectors] = useState<string[]>([]);
  const [selectedSector, setSelectedSector] = useState<string>("all");
  const [selectedIndex, setSelectedIndex] = useState<string>("");
  const [showAllSectors, setShowAllSectors] = useState(false);

  const user = getLocalStorageUser();
  const token = user?.token;

  // Trigger fetch when selection changes
  useEffect(() => {
    if (selectedUniverse) {
      fetchUniverseStocks(selectedUniverse.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUniverse?.id, fetchUniverseStocks]);

  useEffect(() => {
    if (selectedUniverse && !isDirty) {
      setDraftStocks(universeStocks[selectedUniverse.id] || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUniverse?.id, universeStocks, isDirty]);

  // Set initial selected universe
  useEffect(() => {
    if (universes.length > 0 && !selectedUniverse) {
      setSelectedUniverse(universes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universes.length]);

  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const response = await fetchWrapper.get(
          "/api/stocks/sectors",
          undefined,
          {
            Authorization: `Bearer ${token}`,
          },
        );
        const sectors = response?.data || [];
        setSectors(sectors);
      } catch (e) {
        console.error("Failed to load sectors", e);
      }
    };
    fetchSectors();
  }, [token]);

  const handleCreateUniverse = async () => {
    const trimmedName = newUniverseName.trim();
    if (!trimmedName) return;
    const isDuplicate = universes.some(
      (u) => u.name.toLowerCase() === trimmedName.toLowerCase(),
    );

    if (isDuplicate) {
      alertService.error("A universe with this name already exists.");
      return;
    }

    if (universes.length >= MAX_UNIVERSES) {
      alertService.error(
        `You can create a maximum of ${MAX_UNIVERSES} custom universes.`,
      );
      return;
    }

    try {
      const response = await fetchWrapper.post(
        "/api/users/universes",
        { name: trimmedName },
        { Authorization: `Bearer ${token}` },
      );

      if (response?.success) {
        const newUniverse = response.data;
        setUniverses([newUniverse, ...universes]);
        setUniverseStocks((prev) => ({ ...prev, [newUniverse.id]: [] }));
        setNewUniverseName("");
        setShowCreateForm(false);
        setSelectedUniverse(newUniverse);
        setShowOnlySelected(false);
        onMutate?.();
      }
    } catch (e) {
      console.error("Failed to create universe", e);
    }
  };

  const handleUniverseSelection = (universe: Universe) => {
    if (selectedUniverse?.id === universe.id) return;
    handleActionWithUnsavedCheck(
      () => {
        setSelectedUniverse(universe);
        setDraftStocks(universeStocks[universe.id] || []);
        setIsDirty(false);
        setShowOnlySelected(false);
      },
      <React.Fragment>
        switching to{" "}
        <span className="font-semibold">&quot;{universe.name}&quot;</span>
      </React.Fragment>,
    );
  };

  const confirmDeleteUniverse = (universe: Universe) => {
    setUniverseToDelete(universe);
  };

  const handleDeleteUniverse = async () => {
    if (!universeToDelete) return;

    try {
      const response = await fetchWrapper.delete(
        `/api/users/universes/${universeToDelete.id}`,
        undefined,
        { Authorization: `Bearer ${token}` },
      );

      if (response?.success) {
        setUniverses(universes.filter((u) => u.id !== universeToDelete.id));
        if (selectedUniverse?.id === universeToDelete.id) {
          setSelectedUniverse(null);
          setShowOnlySelected(false);
        }
        alertService.success("Universe deleted successfully.");
        onMutate?.();
      }
    } catch (e) {
      console.error("Failed to delete universe", e);
      alertService.error("Failed to delete universe.");
    } finally {
      setUniverseToDelete(null);
    }
  };

  const handleUpdateUniverseName = async (id: string, name: string) => {
    const trimmedName = name.trim();

    // 1. Basic empty check
    if (!trimmedName) return;

    // 2. Character limit check (safety check)
    if (trimmedName.length > 15) {
      alertService.error("Universe name cannot exceed 15 characters.");
      return;
    }

    // 3. Uniqueness check (excluding the universe currently being edited)
    const isDuplicate = universes.some(
      (u) => u.id !== id && u.name.toLowerCase() === trimmedName.toLowerCase(),
    );

    if (isDuplicate) {
      alertService.error("A universe with this name already exists.");
      return;
    }

    try {
      const response = await fetchWrapper.put(
        `/api/users/universes/${id}`,
        { name: trimmedName },
        { Authorization: `Bearer ${token}` },
      );

      if (response?.success) {
        setUniverses(
          universes.map((u) => (u.id === id ? { ...u, name: trimmedName } : u)),
        );
        if (selectedUniverse?.id === id) {
          setSelectedUniverse((prev) =>
            prev ? { ...prev, name: trimmedName } : null,
          );
        }
        setEditingId(null);
        onMutate?.();
      }
    } catch (e) {
      console.error("Failed to update universe name", e);
    }
  };

  // Stock Search Logic
  useEffect(() => {
    const fetchStocks = async () => {
      if (!token) return;
      setIsSearching(true);
      try {
        const queryParams = new URLSearchParams();
        if (searchQuery.trim()) {
          queryParams.set("name", searchQuery.trim());
        }
        if (selectedSector !== "all") {
          queryParams.set("sector", selectedSector);
        }
        if (selectedIndex !== "") {
          queryParams.set("index", selectedIndex);
        }
        const url = "/api/stocks/search".concat(`?${queryParams.toString()}`);

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (data?.data) {
          // Filter out duplicates if any
          const uniqueStocks = Array.from(
            new Map(data.data.map((s: Stock) => [s.symbol, s])).values(),
          ) as Stock[];

          setSearchResults(uniqueStocks);
          setCurrentPage(1); // Reset page on new fetch
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error("Error fetching stocks:", error);
      } finally {
        setIsSearching(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchStocks();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, selectedSector, selectedIndex, token]);

  // Reset page and per-page size whenever the user switches between selected/search views
  useEffect(() => {
    setCurrentPage(1);
    setItemsPerPage(showOnlySelected ? 10 : 50);
  }, [showOnlySelected]);

  useEffect(() => {
    stockListRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage, itemsPerPage]);

  // Reset page and per-page size whenever the user switches between selected/search views
  useEffect(() => {
    setCurrentPage(1);
    setItemsPerPage(showOnlySelected ? 10 : 50);
  }, [showOnlySelected]);

  useEffect(() => {
    stockListRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage, itemsPerPage]);

  const isInUniverse = useCallback(
    (symbol: string) => {
      if (!selectedUniverse) return false;
      return draftStocks.some((s) => s.symbol === symbol);
    },
    [selectedUniverse, draftStocks],
  );

  const handleToggleStock = (stock: Stock) => {
    if (!selectedUniverse) {
      alertService.error("Please create or select a universe to add stock");
      return;
    }

    setIsDirty(true);

    const isCurrentlyInDraft = isInUniverse(stock.symbol);

    if (isCurrentlyInDraft) {
      setDraftStocks((prev) => prev.filter((s) => s.symbol !== stock.symbol));
    } else {
      if (draftStocks.length >= MAX_STOCKS_PER_UNIVERSE) {
        alertService.error(
          `Limit reached! You can only add up to ${MAX_STOCKS_PER_UNIVERSE} stocks to a universe.`,
        );
        return;
      }

      setDraftStocks((prev) => [...prev, stock]);
    }
  };

  const handleSave = async () => {
    if (!token || !selectedUniverse) return;
    setIsSaving(true);

    try {
      const universeId = selectedUniverse.id;
      const currentStocks = universeStocks[universeId] || [];

      const currentSymbols = new Set(currentStocks.map((s) => s.symbol));
      const draftSymbols = new Set(draftStocks.map((s) => s.symbol));

      const addedStocks = draftStocks.filter(
        (s) => !currentSymbols.has(s.symbol),
      );
      const removedStocks = currentStocks.filter(
        (s) => !draftSymbols.has(s.symbol),
      );

      if (addedStocks.length > 0 || removedStocks.length > 0) {
        const addedSymbols = addedStocks.map((s) => s.symbol);
        const removedSymbols = removedStocks.map((s) => s.symbol);

        const response = await fetchWrapper.put(
          "/api/users/universe-stocks",
          {
            universe_id: universeId,
            added: addedSymbols,
            removed: removedSymbols,
          },
          { Authorization: `Bearer ${token}` },
        );

        if (!response?.success) {
          throw new Error(response?.message || "Failed to sync stocks");
        }
      }

      setUniverseStocks((prev) => ({
        ...prev,
        [universeId]: draftStocks,
      }));

      setUniverses((prev) =>
        prev.map((u) =>
          u.id === universeId ? { ...u, stocks_count: draftStocks.length } : u,
        ),
      );

      setIsDirty(false);
      alertService.success("Changes saved successfully.");
      onMutate?.();

      if (pendingActionRef.current) {
        pendingActionRef.current();
        pendingActionRef.current = null;
        setShowUnsavedModal(false);
      }
    } catch (error) {
      console.error("Failed to save changes:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const sortedUniverses = useMemo(() => {
    return [...universes].sort((a, b) => a.name.localeCompare(b.name));
  }, [universes]);

  useEffect(() => {
    if (sortedUniverses.length > 0 && !selectedUniverse) {
      setSelectedUniverse(sortedUniverses[0]);
    }
  }, [sortedUniverses, selectedUniverse]);

  const renderUniverseList = () => (
    <div className="flex-1 flex flex-col overflow-hidden border-r border-[#d0d4e4] dark:border-[#394564] bg-white dark:bg-gray-900/90">
      <div className="p-4 border-b border-[#d0d4e4] dark:border-[#394564] bg-white dark:bg-gray-900/90 shrink-0">
        <div className="flex items-center gap-2 mb-4">
          <Briefcase className="w-5 h-5 text-[#00A85F] dark:text-[#14D83E]" />
          <h2 className="text-lg font-semibold text-black dark:text-white font-figtree">
            Custom Universes
          </h2>
        </div>

        {showCreateForm && (
          <div className="mb-4 p-3 bg-white dark:bg-transparent rounded-lg border border-[#e2e8f0] dark:border-[#394564] shadow-sm">
            <input
              type="text"
              placeholder="Universe Name"
              maxLength={15}
              value={newUniverseName}
              onChange={(e) => setNewUniverseName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent text-sm focus:ring-2 focus:ring-[#00A85F] outline-none text-black dark:text-white mb-2"
              onKeyDown={(e) => e.key === "Enter" && handleCreateUniverse()}
              autoFocus
            />

            <div className="flex gap-2">
              <button
                onClick={handleCreateUniverse}
                disabled={
                  !newUniverseName.trim() || universes.length >= MAX_UNIVERSES
                }
                className="cursor-pointer flex-1 bg-[#00A85F] hover:bg-[#009252] text-white text-sm py-1.5 rounded-md transition-colors disabled:opacity-50"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setNewUniverseName("");
                }}
                className="cursor-pointer flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm py-1.5 rounded-md transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!showCreateForm && universes.length < MAX_UNIVERSES && (
          <button
            onClick={() =>
              handleActionWithUnsavedCheck(
                () => setShowCreateForm(true),
                "creating a new universe",
              )
            }
            className="cursor-pointer w-full flex items-center justify-center gap-2 bg-[#00A85F] hover:bg-[#009252] text-white rounded-md py-2 px-4 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Create Universe ({universes.length}/{MAX_UNIVERSES})
          </button>
        )}

        {universes.length >= MAX_UNIVERSES && !showCreateForm && (
          <div className="text-sm text-amber-600 dark:text-amber-400 font-medium text-center p-2 bg-amber-50 dark:bg-amber-900/20 rounded-md border border-amber-200 dark:border-amber-800">
            Limit reached ({MAX_UNIVERSES}/{MAX_UNIVERSES})
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {universes.length === 0 ? (
          <div className="p-8 text-center">
            <Folder className="w-10 h-10 mx-auto mb-2 text-gray-400 opacity-50" />
            <p className="text-sm text-gray-500 mb-4">No universes found.</p>
          </div>
        ) : (
          <div className="divide-y divide-[#edf2f7] dark:divide-[#2d3748]">
            {sortedUniverses.map((universe) => {
              const isSelected = selectedUniverse?.id === universe.id;

              return (
                <div
                  key={universe.id}
                  className={`p-3 cursor-pointer transition-colors ${isSelected ? "bg-[#e6f6ee] dark:bg-[#00A85F]/10 border-l-4 border-[#00A85F]" : "hover:bg-gray-50 dark:hover:bg-gray-800 border-l-4 border-transparent"}`}
                  onClick={() => handleUniverseSelection(universe)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 overflow-hidden pr-2">
                      <div className="flex items-center gap-2">
                        <Folder
                          className={`w-4 h-4 shrink-0 ${isSelected ? "text-[#00A85F]" : "text-gray-400"}`}
                        />
                        {editingId === universe.id ? (
                          <input
                            type="text"
                            value={editName}
                            maxLength={15}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter")
                                handleUpdateUniverseName(universe.id, editName);
                              if (e.key === "Escape") setEditingId(null);
                            }}
                            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-500 rounded px-2 py-1 text-sm text-black dark:text-white"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <h3
                            className={`font-semibold text-sm truncate ${isSelected ? "text-[#00A85F] dark:text-[#14D83E]" : "text-black dark:text-white"}`}
                          >
                            {toTitleCase(universe.name)}
                          </h3>
                        )}
                      </div>

                      <span className="text-xs text-[#00A85F] font-medium pl-6 mt-1 block">
                        {(() => {
                          const stocksArray =
                            universeStocks[String(universe.id)];
                          const count = stocksArray
                            ? stocksArray.length
                            : universe.stocks_count || 0;

                          return (
                            <>
                              {count}{" "}
                              {count === 1 || count === 0 ? "stock" : "stocks"}
                            </>
                          );
                        })()}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      {editingId === universe.id ? (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateUniverseName(universe.id, editName);
                            }}
                            className="cursor-pointer p-1 text-[#00A85F] hover:bg-[#00A85F]/10 rounded"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingId(null);
                            }}
                            className="cursor-pointer p-1 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleActionWithUnsavedCheck(
                                () => {
                                  setEditingId(universe.id);
                                  setEditName(universe.name);
                                },
                                <React.Fragment>
                                  editing{" "}
                                  <span className="font-semibold">
                                    &quot;{universe.name}&quot;
                                  </span>
                                </React.Fragment>,
                              );
                            }}
                            className="cursor-pointer p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleActionWithUnsavedCheck(
                                () => confirmDeleteUniverse(universe),
                                <React.Fragment>
                                  deleting{" "}
                                  <span className="font-semibold">
                                    &quot;{universe.name}&quot;
                                  </span>
                                </React.Fragment>,
                              );
                            }}
                            className="cursor-pointer p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const currentUniverseStocks = draftStocks;

  const filteredUniverseStocks = currentUniverseStocks.filter(
    (s) =>
      s.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.company_name &&
        s.company_name.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const currentStocksList =
    selectedUniverse && showOnlySelected
      ? filteredUniverseStocks
      : searchResults;

  const paginatedStocks = currentStocksList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const visibleSectors = showAllSectors ? sectors : sectors.slice(0, 6);

  const renderStockSelector = () => (
    <div className="flex-[2] flex flex-col bg-white dark:bg-gray-900/90 overflow-hidden relative">
      <div className="p-4 border-b border-[#d0d4e4] dark:border-[#394564] bg-white dark:bg-gray-900/90 dark:bg-[#1a1d24] shrink-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
            <div>
              <h2 className="text-lg font-semibold text-black dark:text-white font-figtree">
                {selectedUniverse && !showOnlySelected
                  ? `Select Stocks for ${toTitleCase(selectedUniverse.name)}`
                  : selectedUniverse && showOnlySelected
                    ? `Selected Stocks for ${toTitleCase(selectedUniverse.name)}`
                    : "Search & Browse Stocks"}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {selectedUniverse && showOnlySelected
                  ? "Managing selected stocks"
                  : selectedUniverse
                    ? "Search and add stocks to your universe"
                    : "Search and explore stocks across sectors"}
              </p>
            </div>
          </div>
          {selectedUniverse && (
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              <button
                onClick={() => setShowOnlySelected(!showOnlySelected)}
                className={`cursor-pointer flex items-center gap-2 text-sm px-2 py-1 rounded-lg font-medium transition-all shadow-sm ${
                  showOnlySelected
                    ? "bg-[#00A85F] text-white"
                    : "bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 text-[#00A85F] hover:bg-[#00A85F]/5"
                }`}
              >
                <Check
                  className={`w-3 h-3 ${showOnlySelected ? "block" : "hidden"}`}
                />
                {isFetchingStocks ? "..." : draftStocks.length} / 50 Selected
              </button>
              {selectedUniverse && (!showOnlySelected || hasUnsavedChanges) && (
                <>
                  {hasUnsavedChanges && (
                    <button
                      onClick={() => {
                        setDraftStocks(originalStocks);
                        setIsDirty(false);
                      }}
                      className="cursor-pointer px-2 py-1 text-sm font-medium text-amber-600 dark:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-md transition-colors"
                    >
                      Discard
                    </button>
                  )}

                  <button
                    onClick={handleSave}
                    disabled={!hasUnsavedChanges || isSaving}
                    className={`px-2 py-1 text-sm font-medium text-white rounded-md transition-colors flex items-center gap-2 shadow-sm ${
                      !hasUnsavedChanges || isSaving
                        ? "bg-[#00A85F]/30 cursor-not-allowed"
                        : "bg-[#00A85F] hover:bg-[#009252] cursor-pointer"
                    }`}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={
              selectedUniverse && showOnlySelected
                ? "Search from selected stocks..."
                : "Search stock symbol or name..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-10 py-2.5 bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A85F] text-sm text-black dark:text-white transition-all font-figtree"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="cursor-pointer absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filters */}
        {!showOnlySelected && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Filter className="w-4 h-4 text-gray-400 shrink-0" />
            <button
              onClick={() => {
                setSelectedSector("all");
                setSelectedIndex("");
              }}
              className={`cursor-pointer px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                selectedSector === "all"
                  ? "bg-[#00A85F] text-white"
                  : "bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              All
            </button>
            {indices.map((index) => (
              <button
                key={index}
                onClick={() => {
                  setSelectedSector("");
                  setSelectedIndex(index);
                }}
                className={`cursor-pointer px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedIndex === index
                    ? "bg-[#00A85F] text-white"
                    : "bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                {index}
              </button>
            ))}
            {/* Vertical Separator */}
            <div className="h-5 w-[1px] bg-gray-300 dark:bg-gray-700 hidden sm:block" />
            <div className="flex flex-wrap items-center gap-2">
              {visibleSectors.map((sector) => (
                <button
                  key={sector}
                  onClick={() => {
                    setSelectedSector(sector);
                    setSelectedIndex("");
                  }}
                  className={`cursor-pointer px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    selectedSector === sector
                      ? "bg-[#00A85F] text-white"
                      : "bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  {sector}
                </button>
              ))}
              {sectors.length > 6 && (
                <button
                  onClick={() => setShowAllSectors(!showAllSectors)}
                  className="cursor-pointer px-3 py-1 rounded-full text-xs font-medium text-[#00A85F] hover:bg-[#00A85F]/10 transition-colors flex items-center gap-1 border border-gray-200 dark:border-gray-700"
                >
                  <span>
                    {showAllSectors ? "Less" : `+${sectors.length - 6} More`}
                  </span>
                  <span
                    className={`transition-transform duration-200 ${showAllSectors ? "rotate-180" : ""}`}
                  >
                    ↓
                  </span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        ref={stockListRef}
        className={`flex-1 overflow-y-auto ${showOnlySelected ? "p-4" : ""} relative`}
      >
        {isSearching || isFetchingStocks ? (
          <div className="flex justify-center items-center h-full p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00A85F]"></div>
          </div>
        ) : currentStocksList.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
            <Search className="w-10 h-10 mb-4 opacity-30" />
            <p className="font-medium text-black dark:text-white">
              {searchQuery ? "No stocks found" : "No stocks available"}
            </p>
            <p className="text-sm mt-1">
              {searchQuery
                ? `Try adjusting "${searchQuery}"`
                : "Use the search bar above to find stocks."}
            </p>
          </div>
        ) : showOnlySelected ? (
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto content-start">
              <div className="flex flex-wrap gap-2.5">
                {currentStocksList.map((stock) => (
                  <div
                    key={stock.symbol}
                    className="group inline-flex items-center gap-2 py-1.5 pl-3 pr-1.5 bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-full shadow-sm hover:border-[#00A85F] dark:hover:border-[#00A85F] transition-colors"
                  >
                    <span
                      className="font-semibold text-xs text-black dark:text-white max-w-[120px] truncate"
                      title={stock.symbol}
                    >
                      {stock.symbol}
                    </span>
                    <button
                      onClick={() => handleToggleStock(stock)}
                      className="cursor-pointer p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-full transition-colors"
                      title="Remove from universe"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {currentStocksList.length === 0 && (
              <div className="flex-1 py-12 flex flex-col items-center justify-center text-gray-500">
                <Search className="w-8 h-8 mb-3 opacity-30" />
                <p className="font-medium text-black dark:text-white text-sm">
                  {searchQuery
                    ? "Not in your universe"
                    : "Your universe is empty"}
                </p>
                <p className="text-xs mt-1 max-w-sm text-center mb-4">
                  {searchQuery
                    ? `"${searchQuery}" is not in this universe. Switch to the main list to find and add it.`
                    : "Switch to the main view to start adding stocks!"}
                </p>
                <button
                  onClick={() => setShowOnlySelected(false)}
                  className="cursor-pointer px-4 py-2 bg-[#00A85F] hover:bg-[#009252] text-white text-xs font-medium rounded-md transition-colors"
                >
                  Go to full stock list
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="divide-y divide-[#edf2f7] dark:divide-[#2d3748]">
            {paginatedStocks.map((stock) => {
              const inUniverse = isInUniverse(stock.symbol);
              const isHovered = hoveredStockId === stock.symbol;

              return (
                <div
                  key={stock.symbol}
                  onMouseEnter={() => setHoveredStockId(stock.symbol)}
                  onMouseLeave={() => setHoveredStockId(null)}
                  onClick={() => handleToggleStock(stock)}
                  className={`px-6 py-3 flex items-center justify-between transition-colors cursor-pointer ${
                    isHovered
                      ? "bg-gray-50 dark:bg-gray-800/50"
                      : "bg-transparent"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                        inUniverse
                          ? "border-[#00A85F] bg-[#00A85F]"
                          : "border-gray-300 dark:border-gray-500"
                      }`}
                    >
                      {inUniverse && (
                        <Check className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-black dark:text-white font-mono text-sm">
                          {stock.symbol}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                        {stock.company_name || "Unknown Company"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {currentStocksList.length > 0 && !showOnlySelected && (
        <Pagination
          currentPage={currentPage}
          totalItems={currentStocksList.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onRowsChange={setItemsPerPage}
          pageSizeOptions={[50, 100, 150]}
          compact={true}
        />
      )}
    </div>
  );

  return (
    <>
      <div className="flex xl:h-[70vh] h-[65vh] w-full border border-[#d0d4e4] dark:border-[#394564] rounded-lg overflow-hidden bg-white dark:bg-[#121419]">
        {renderUniverseList()}
        {renderStockSelector()}
      </div>

      {universeToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900/90 p-6 rounded-lg shadow-xl max-w-sm w-full border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-bold text-black dark:text-white mb-2">
              Delete Universe?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                &quot;{universeToDelete.name}&quot;
              </span>
              ? This action cannot be undone and will remove all selected
              stocks.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setUniverseToDelete(null)}
                className="cursor-pointer px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUniverse}
                className="cursor-pointer px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showUnsavedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 p-5 rounded-lg shadow-xl max-w-sm w-full border border-gray-200 dark:border-gray-700 relative">
            <button
              onClick={() => {
                pendingActionRef.current = null;
                setShowUnsavedModal(false);
              }}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold text-black dark:text-white mb-2 pr-6">
              Unsaved Changes
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
              You have unsaved changes in your current universe. Do you want to
              save them before {pendingActionMessage}?
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="cursor-pointer flex-1 px-3 py-2 text-sm font-medium text-white bg-[#00A85F] hover:bg-[#009252] rounded-md transition-colors text-center"
              >
                {isSaving ? "Saving..." : "Save & Continue"}
              </button>
              <button
                onClick={() => {
                  setDraftStocks(originalStocks);
                  setIsDirty(false);
                  setShowUnsavedModal(false);
                  if (pendingActionRef.current) {
                    pendingActionRef.current();
                    pendingActionRef.current = null;
                  }
                }}
                disabled={isSaving}
                className="cursor-pointer flex-1 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-md transition-colors text-center"
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default React.memo(CustomUniverseManager);
